import * as _ from 'lodash';

import { Document } from 'src/app/people/models/document';
import { Education } from 'src/app/people/models/education';
import { DemographicInfo } from 'src/app/people/models/demographicInfo';
import { Position } from 'src/app/shared/models/team/position';
import { Image } from 'src/app/people/models/image';
import { Note } from 'src/app/people/models/note';
import { Tag } from 'src/app/people/models/tag';
import { Attribute } from 'src/app/shared/models/attributes/attribute';
import { SeatStatus } from 'src/app/shared/models/team/seatStatus';
import { PersonApplication } from 'src/app/shared/models/application/personApplication';
import { PersonStatus } from 'src/app/people/models/personStatus';
import { PersonDisclaimer } from 'src/app/people/models/personDisclaimer';
import { Disclaimer } from 'src/app/shared/models/disclaimer';
import { StringIgnoreCaseComparator } from 'src/app/shared/utils/comparators';
import { TeamHistory } from './teamHistory';
import { PersonCertification } from './personCertification';
import { IFeaturedPersonCustomField } from './featuredPearsonCutomField.model';
import { PersonEvaluationSummary } from 'src/app/shared/models/evaluation/personEvaluationSummary';
import { Seat } from 'src/app/shared/models/team/seat';

export class Person {
  public id: string;
  public firstName: string;
  public lastName: string;
  public middleName: string;
  public suffix: string;
  public fullName: string;
  public email: string;
  public linkedInProfile: string;
  public jobTitle: string;
  public company: string;
  public image: Image;
  public phoneNumber: string;
  public extension: string;
  public rating: number;
  public documents: Document[] = new Array<Document>();
  public education: Education[] = new Array<Education>();
  public notes: Note[] = new Array<Note>();
  public evaluationSummary: PersonEvaluationSummary[] = new Array<PersonEvaluationSummary>();
  public demographicInfo: DemographicInfo = new DemographicInfo();
  public tags: Tag[];
  public get teamCount(): number {
    return this.teamHistory ? this.teamHistory.length : 0;
  }
  public activeTeamsCount: number;
  public activeTeamsCountInCurrentWorkspace: number;
  public attributes: Attribute[] = new Array<Attribute>();
  public applications: PersonApplication[] = new Array<PersonApplication>();
  public disclaimers: PersonDisclaimer[] = new Array<PersonDisclaimer>();
  public teamHistory: TeamHistory[] = new Array<TeamHistory>();
  public personCertifications: PersonCertification[] = new Array<PersonCertification>();
  public updatedOn: Date;
  public status: string;
  public metadata: any;
  public featuredCustomFields: IFeaturedPersonCustomField[];
  public selected: boolean;
  public seatId: string;
  public isUnsubscribed: boolean;

  constructor(person: any = undefined) {
    if (person) this.setProperties(person);
  }
  public get certMetricsId(): string {
    return this.metadata?.certMetricsId;
  }

  public set certMetricsId(value: string) {
    this.metadata = this.metadata ?? {};
    this.metadata.certMetricsId = value;
  }
  isArchived() {
    return this.status == PersonStatus.Archived;
  }

  isActive() {
    return this.status == PersonStatus.Active;
  }

  isPotential() {
    return this.status == PersonStatus.Potential;
  }

  isPersonOnTeam() {
    return this.teamCount > 0;
  }

  isPersonOnActiveTeam() {
    return this.activeTeamsCountInCurrentWorkspace > 0;
  }

  setEducation(education: Education[]) {
    this.education = education;
  }

  setEvaluations(evaluationSummary: PersonEvaluationSummary[]) {
    this.evaluationSummary = evaluationSummary.sort((obj1, obj2) => {
      return obj2.lastEvaluationResponseDate < obj1.lastEvaluationResponseDate
        ? -1
        : obj2.lastEvaluationResponseDate > obj1.lastEvaluationResponseDate
        ? 1
        : 0;
    });
  }
  setNotes(notes: Note[]) {
    this.notes = notes;
  }

  setTeamHistory(teamHistory: TeamHistory[]) {
    this.teamHistory = teamHistory;
  }

  addTeamHistory(teamHistory: TeamHistory) {
    this.teamHistory.push(teamHistory);
    this.teamHistory = _.orderBy(
      this.teamHistory,
      [
        (t) => {
          return t.dateRange.startDate || '';
        },
      ],
      ['desc']
    );
  }
  updateTeamHistory(teamHistory: TeamHistory) {
    let index = this.teamHistory.findIndex((x) => x.id == teamHistory.id);
    if (index == -1) return;
    this.teamHistory[index] = teamHistory;
    this.teamHistory = _.orderBy(
      this.teamHistory,
      [
        (t) => {
          return t.dateRange.startDate || '';
        },
      ],
      ['desc']
    );
  }
  deleteTeamHistory(historicalTeamId: string) {
    _.remove(this.teamHistory, function (teamHistory) {
      return teamHistory.id == historicalTeamId;
    });
  }
  setCertifications(personCertifications: PersonCertification[]) {
    this.personCertifications = personCertifications;
  }

  addNote(note: Note) {
    this.notes.unshift(note);
  }

  private setImage(person: any) {
    if (person.image) {
      this.image = person.image;
    } else {
      this.image = new Image(person.imageId);
    }
  }

  private setTags(tags: Tag[]) {
    this.tags = _.map(tags, function (tag) {
      return new Tag(tag);
    });
  }

  addTag(tag: Tag) {
    this.tags.push(tag);
    this.tags = _.sortBy(this.tags, 'text');
  }

  setApplications(applications: PersonApplication[]) {
    this.applications = _.map(applications, (app) => PersonApplication.parse(app));
  }

  setDisclaimers(disclaimers: PersonDisclaimer[]) {
    this.disclaimers = _.map(disclaimers, (disclaimer) => PersonDisclaimer.parse(disclaimer));
  }

  getPersonDisclaimer(disclaimer: Disclaimer) {
    return _.find(this.disclaimers, function (personDisclaimer) {
      if (disclaimer.id === personDisclaimer.disclaimer.id) return true;
    });
  }

  hasDisclaimer(disclaimer: Disclaimer): boolean {
    return !_.isNil(this.getPersonDisclaimer(disclaimer));
  }

  deleteDocument(documentToRemove: Document) {
    _.remove(this.documents, function (document) {
      return documentToRemove.key == document.key;
    });
  }

  deleteNote(noteToRemove: Note) {
    _.remove(this.notes, function (note) {
      return noteToRemove.id == note.id;
    });
  }

  hasEducation() {
    return this.education && this.education.length;
  }

  hasGender() {
    return this.demographicInfo && this.demographicInfo.gender;
  }

  hasRace() {
    return (
      this.demographicInfo && this.demographicInfo.races && this.demographicInfo.races.length > 0
    );
  }

  hasImage() {
    return this.image && this.image.id;
  }

  hasNotes() {
    return this.notes && this.notes.length;
  }

  hasDocuments() {
    return this.documents && this.documents.length;
  }

  hasApplications(): boolean {
    return this.applications.length > 0;
  }

  getAttributesByFeatured(featured: boolean): Attribute[] {
    return _.filter(this.attributes, function (attribute) {
      return attribute.featured === featured;
    });
  }

  isMaleGender() {
    return (
      this.demographicInfo &&
      this.demographicInfo.gender &&
      this.demographicInfo.gender.toLowerCase() === 'male'
    );
  }

  isFemaleGender() {
    return (
      this.demographicInfo &&
      this.demographicInfo.gender &&
      this.demographicInfo.gender.toLowerCase() === 'female'
    );
  }

  isNonBinaryGender() {
    return (
      this.demographicInfo &&
      this.demographicInfo.gender &&
      this.demographicInfo.gender.toLowerCase() === 'nonbinary'
    );
  }

  isUnknownGender() {
    return (
      !this.isMaleGender() &&
      !this.isFemaleGender() &&
      !this.isNonBinaryGender() &&
      !this.isPreferNotToAnswerGender()
    );
  }

  isPreferNotToAnswerGender() {
    return (
      this.demographicInfo &&
      this.demographicInfo.gender &&
      this.demographicInfo.gender.toLowerCase() === 'prefernottoanswer'
    );
  }

  getSeatInformation(positions: Position[]): Seat {
    let person = this;
    let selectedSeat: Seat = null;
    positions?.forEach((position) => {
      position.seats?.forEach((seat) => {
        if (seat.person?.id === person?.id) {
          selectedSeat = seat;
        }
      });
    });
    return selectedSeat;
  }

  seatStatus(positions: Position[]): SeatStatus {
    let person = this;
    let status: SeatStatus = null;
    positions?.forEach((position) => {
      position.seats?.forEach((seat) => {
        if (seat.person?.id === person?.id) {
          status = seat.status;
        }
      });
    });
    return status;
  }

  seatStatusDescription(positions: Position[]): string {
    return SeatStatus[this.seatStatus(positions)];
  }

  isPicked(positions: Position[]): boolean {
    return this.seatStatus(positions) === SeatStatus.Picked;
  }

  isInvited(positions: Position[]): boolean {
    return this.seatStatus(positions) === SeatStatus.Invited;
  }

  isNoResponse(positions: Position[]): boolean {
    return this.seatStatus(positions) === SeatStatus.NoResponse;
  }

  isAccepted(positions: Position[]): boolean {
    return this.seatStatus(positions) === SeatStatus.Accepted;
  }

  isPickedOrInvitedOrAcceptedOrDeclined(positions: Position[]): boolean {
    const seatStatus = this.seatStatus(positions);
    return (
      seatStatus === SeatStatus.Picked ||
      seatStatus === SeatStatus.Invited ||
      seatStatus === SeatStatus.NoResponse ||
      seatStatus === SeatStatus.Accepted ||
      seatStatus === SeatStatus.Declined ||
      seatStatus === SeatStatus.NoShow
    );
  }

  showPositionDropdown(positions: Position[]): boolean {
    return (
      this.isActive() ||
      (this.isArchived() && this.isPickedOrInvitedOrAcceptedOrDeclined(positions))
    );
  }

  isDrafted(positions: Position[]): boolean {
    return _.some(positions, (position) =>
      _.some(position.seats, (seat) => (seat.person ? seat.person.id === this.id : false))
    );
  }

  getPhoneNumberForDisplay() {
    if (Number.parseInt(this.phoneNumber).toString().length === this.phoneNumber.length) {
      return `(${this.phoneNumber.substring(0, 3)}) ${this.phoneNumber.substring(
        3,
        6
      )}-${this.phoneNumber.substring(6, 10)}`;
    }

    return this.phoneNumber ? this.phoneNumber : '';
  }

  hasAllTags(tags: Tag[]) {
    return _.differenceBy(tags, this.tags, 'id').length === 0;
  }

  hasRating() {
    return this.rating && this.rating > 0;
  }

  getStarRatingPercentage() {
    return (this.rating / 5) * 100;
  }

  setAttribute(attributeDefinitionId: string, attributes: Attribute[]) {
    let filtered = this.attributes.filter(
      (attribute) => attribute.attributeDefinitionId != attributeDefinitionId
    );
    let merged = filtered.concat(
      attributes.filter((attribute) => attribute.attributeDefinitionId == attributeDefinitionId)
    );
    let ordered = merged.sort(StringIgnoreCaseComparator('name'));
    this.attributes = ordered;
  }

  private setAttributes(attributes) {
    this.attributes = _.orderBy(
      _.map(attributes, function (attribute) {
        return Attribute.parse(attribute);
      }),
      'name'
    );
    this.setFeaturedCustomFields();
  }

  private setFeaturedCustomFields() {
    if (this.attributes && this.attributes.length) {
      this.featuredCustomFields = this.attributes.filter((x) => x.featured);
    } else {
      this.featuredCustomFields = [];
    }
  }

  hasCompletedApplication(application): boolean {
    return _.some(this.applications, function (app) {
      return app.id === application.id && app.isCompleted;
    });
  }

  hasCompletedApplications(): boolean {
    return _.some(this.applications, (app) => app.isCompleted);
  }

  getCompletedApplications(): PersonApplication[] {
    return _.filter(this.applications, (app) => app.isCompleted);
  }

  getInCompleteApplications(): PersonApplication[] {
    return _.filter(this.applications, (app) => !app.isCompleted);
  }

  getLightVersion() {
    let personalInfo = new Person(this);
    personalInfo.setAttributes([]);
    personalInfo.setApplications([]);
    personalInfo.setTags([]);
    personalInfo.setDisclaimers([]);
    personalInfo.demographicInfo = null;
    return personalInfo;
  }

  get numOfAllPersonCertifications(): number {
    return this.personCertifications ? this.personCertifications.length : 0;
  }
  get numOfActivePersonCertifications(): number {
    return this.personCertifications
      ? this.personCertifications.filter((c) => c.isActive).length
      : 0;
  }

  /** consolidated common code from setProperties(person) and update(person) */
  private setSharedFields(person: any, fullName: string = null) {
    this.id = person.id;
    this.firstName = person.firstName;
    this.lastName = person.lastName;
    this.middleName = person.middleName;
    if (fullName) {
      this.fullName = fullName;
    } else {
      const middleName = person.middleName ? ` ${person.middleName} ` : ' ';
      const suffix = person.suffix ? ` ${person.suffix}` : '';
      this.fullName = `${person.firstName}${middleName}${person.lastName}${suffix}`;
    }
    this.email = person.email;
    this.phoneNumber = person.phoneNumber;
    this.extension = person.extension;
    this.linkedInProfile = person.linkedInProfile;
    this.jobTitle = person.jobTitle;
    this.company = person.company;
    this.status = person.status;
    this.rating = person.rating;
    this.suffix = person.suffix;
    this.metadata = person.metadata;
    this.updatedOn = person.updatedOn;
    this.activeTeamsCount = person.activeTeamsCount;
    this.activeTeamsCountInCurrentWorkspace = person.activeTeamsCountInCurrentWorkspace;
  }

  private setProperties(person: any) {
    this.setSharedFields(person, person.fullName);
    //move to constructor?
    this.setAttributes(person.customFields);
    this.setImage(person);
    this.demographicInfo = new DemographicInfo(person.demographicInfo);
    this.setTags(person.tags);
    this.setApplications(person.applications);
    this.setDisclaimers(person.disclaimers);
    this.seatId = person.seatId;
    this.isUnsubscribed = person.isUnsubscribed;
  }

  update(person: any) {
    this.setSharedFields(person);
    this.setFeaturedCustomFields();
    this.image = person.image;
    this.demographicInfo = person.demographicInfo;
    this.tags = person.tags;
  }

  static parseForSearch(data: any): Person {
    let p = new Person(data);
    p.activeTeamsCount = data.activeSeats ? data.activeSeats.length : 0;
    return p;
  }
}
